import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase only if not already initialized
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Uncomment for local development with emulator
// connectFunctionsEmulator(functions, "localhost", 5001);

// Check if email already exists
const checkEmailExists = async (email) => {
  const q = query(collection(db, 'email_subscriptions'), where('email', '==', email.toLowerCase()));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};

// Function to save email subscription (with duplicate check)
export const saveEmailSubscription = async (email, source = 'footer') => {
  try {
    const normalizedEmail = email.toLowerCase().trim();

    // Check for duplicate
    const exists = await checkEmailExists(normalizedEmail);
    if (exists) {
      return { success: false, error: 'duplicate', message: 'This email is already subscribed' };
    }

    const docRef = await addDoc(collection(db, 'email_subscriptions'), {
      email: normalizedEmail,
      source: source,
      subscribedAt: serverTimestamp(),
      status: 'active'
    });
    console.log('Email saved with ID:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error saving email:', error);
    return { success: false, error: error.message };
  }
};

// Send verification email via SendGrid (Firebase Function)
export const sendVerificationEmail = async (email, lang = 'en') => {
  try {
    const normalizedEmail = email.toLowerCase().trim();

    // Check if already subscribed
    const exists = await checkEmailExists(normalizedEmail);
    if (exists) {
      return {
        success: false,
        error: 'duplicate',
        message: lang === 'ko' ? '이미 등록된 이메일입니다' : 'This email is already subscribed'
      };
    }

    // Call Firebase Function
    const sendEmailFunction = httpsCallable(functions, 'sendVerificationEmailCallable');
    const result = await sendEmailFunction({
      email: normalizedEmail,
      lang: lang,
      baseUrl: window.location.origin
    });

    return result.data;
  } catch (error) {
    console.error('Error sending verification email:', error);

    // Handle Firebase Functions error
    if (error.code === 'functions/already-exists') {
      return {
        success: false,
        error: 'duplicate',
        message: lang === 'ko' ? '이미 등록된 이메일입니다' : 'This email is already subscribed'
      };
    }

    return {
      success: false,
      error: error.code,
      message: lang === 'ko' ? '이메일 발송에 실패했습니다. 다시 시도해주세요.' : 'Failed to send email. Please try again.'
    };
  }
};

// Check if URL has verification token
export const getVerificationToken = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('verify');
};

// Send welcome email via Firebase Function
export const sendWelcomeEmail = async (email) => {
  try {
    const sendWelcomeFunction = httpsCallable(functions, 'sendWelcomeEmailCallable');
    await sendWelcomeFunction({ email });
    return { success: true };
  } catch (error) {
    console.error('Error sending welcome email:', error);
    return { success: false };
  }
};

// Verify email token (called when user clicks the link)
export const verifyEmailToken = async (token) => {
  try {
    // Get token document
    const tokenRef = doc(db, 'email_tokens', token);
    const tokenDoc = await getDoc(tokenRef);

    if (!tokenDoc.exists()) {
      return { success: false, message: 'Invalid verification link' };
    }

    const tokenData = tokenDoc.data();

    // Check if already used
    if (tokenData.used) {
      return { success: false, message: 'This link has already been used' };
    }

    // Check expiration
    if (tokenData.expiresAt.toDate() < new Date()) {
      return { success: false, message: 'This link has expired' };
    }

    // Mark token as used
    await updateDoc(tokenRef, { used: true });

    // Check if email already subscribed
    const exists = await checkEmailExists(tokenData.email);
    if (!exists) {
      // Add to subscriptions
      await addDoc(collection(db, 'email_subscriptions'), {
        email: tokenData.email,
        source: 'email_verified',
        subscribedAt: serverTimestamp(),
        status: 'active',
        verifiedAt: serverTimestamp()
      });

      // Send welcome email
      await sendWelcomeEmail(tokenData.email);
    }

    return { success: true, email: tokenData.email };
  } catch (error) {
    console.error('Error verifying email:', error);
    return { success: false, message: 'Verification failed. Please try again.' };
  }
};

// Sign up with Google
export const signUpWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const normalizedEmail = user.email.toLowerCase();

    // Check if already subscribed
    const exists = await checkEmailExists(normalizedEmail);
    if (!exists) {
      // Save to subscriptions if new
      await addDoc(collection(db, 'email_subscriptions'), {
        email: normalizedEmail,
        source: 'auth_google',
        subscribedAt: serverTimestamp(),
        status: 'active',
        uid: user.uid,
        displayName: user.displayName
      });

      // Send welcome email for new subscribers
      await sendWelcomeEmail(normalizedEmail);
    }

    return { success: true, user, isNew: !exists };
  } catch (error) {
    console.error('Error signing up with Google:', error);
    let message = 'Google sign up failed. Please try again.';
    if (error.code === 'auth/popup-closed-by-user') {
      message = 'Sign up cancelled';
    }
    return { success: false, error: error.code, message };
  }
};

export { db, auth };
